import { Box, type SxProps, type Theme } from '@mui/material'

/**
 * VRG-736 — the row (or rows) of KPI tiles above a card: one tile a row on a phone, two from
 * `sm`, four from `lg`. GTM → Channels and Sales → Pipeline both render it.
 *
 * ── WHY CSS GRID, NOT MUI `Grid container` ───────────────────────────────────────────
 *
 * MUI v5's `Grid container spacing={n}` lays its gutters out with NEGATIVE margins: the
 * container is pulled up and left by one gutter and made one gutter wider, and every item
 * pads itself back. That only works while nothing else touches the container's margins.
 * A `Stack` WITHOUT `useFlexGap` does: it resets every child's margin to 0 (MUI
 * `createStack.js`, "relies on each child to give up control of the margin"), so the
 * container keeps its extra width but loses the pull-back. The row then starts one gutter
 * to the right of the cards above and below it, and runs one gutter past their right edge.
 *
 * That is exactly what GTM → Channels shipped (its tiles sit in a plain `Stack`), while
 * Sales → Pipeline, with the same tiles inside a `useFlexGap` Stack, happened to line up.
 * A row that aligns or not depending on how its PARENT spaces children is a bug waiting in
 * every new page, so the row owns its own gaps with `gap` and has no margins to lose: it is
 * flush with its parent's content box in any Stack, Box or Card, at every breakpoint.
 *
 * Do not swap it back to `Grid container` (`kpi-tile-grid.guard.spec.ts`).
 */
export const KpiTileGrid = ({
  children,
  sx,
  'data-testid': testId,
}: {
  children: React.ReactNode
  sx?: SxProps<Theme>
  'data-testid'?: string
}) => (
  <Box
    data-testid={testId}
    sx={[
      {
        display: 'grid',
        // 4 on the platform's 4px scale = 16px: the gutter the old Grid used (spacing={4}).
        gap: 4,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {children}
  </Box>
)
